//
// The address follows the rule the invitation status carries (emailRule): free
// after an SMS invite, the company's domain after an emailed one, the invited
// address itself when it belongs to a public mail provider.
//
import {
  useCallback,
  useEffect,
} from 'react';

import {
  Column,
  Row,
  TextInput,
  useWizardContext,
} from '../../../components';
import { useTownAssets } from '../../../stores';
import { regex } from '../../../utils';

import InviteAckRefusal from './InviteAckRefusal';

const emailDomainOf = (email) => (email ?? '').trim().toLowerCase().split('@')[1];

const pageStateValidator = (state) => {
  const valid = {};
  const errors = {};

  valid.fname = regex.firstLastname.test((state.fname ?? '').trim());
  valid.lname = regex.firstLastname.test((state.lname ?? '').trim());
  valid.rlDesc = regex.pxLabel.test((state.rlDesc ?? '').trim());

  const emailRule = state.invite?.emailRule ?? 'exact';
  const email = (state.email ?? '').trim();
  valid.email = (emailRule === 'exact')
    || (regex.email.test(email)
        && ((emailRule === 'free')
            || (emailDomainOf(email) === emailDomainOf(state.invite?.prtnrInvtInfo?.target?.email))));

  const pageValid = valid.fname && valid.lname && valid.rlDesc && valid.email;

  return { valid, errors, pageValid };
};

function InviteAckIdentityPage() {
  const { assets } = useTownAssets();
  const { translate } = assets;
  const {
    setPageInfo,
    setPageState,
    setMustConfirmClose,
    state,
    validation,
  } = useWizardContext();

  const handleChange = useCallback(
    (event) => {
      const { name, value } = event.currentTarget;

      setMustConfirmClose(true);
      setPageState({
        [name]: value,
        [`${name}Touched`]: true,
      });
    },
    [setPageState, setMustConfirmClose],
  );

  const invitedDomain = emailDomainOf(state.invite?.prtnrInvtInfo?.target?.email);

  const handleEmailLocalPartChange = useCallback(
    (event) => {
      const localPart = event.currentTarget.value.replace(/@/g, '').trim();

      setMustConfirmClose(true);
      setPageState({
        email: `${localPart}@${invitedDomain}`,
        emailTouched: true,
      });
    },
    [setPageState, setMustConfirmClose, invitedDomain],
  );

  useEffect(
    () => {
      setPageInfo({
        nextPage: 'team',
        pageStateValidator,
      });
    },
    [setPageInfo],
  );

  const inviteTxtKeys = {
    observer: 'inviterInvitedYouAsObserver',
    onDemand: 'inviterInvitedYouAsOnDemand',
    permanent: 'inviterInvitedYouAsPermanent',
  };
  const inviteTxtKey = inviteTxtKeys[state.invite?.prtnrInvtInfo?.inviteType];
  const emailRule = state.invite?.emailRule ?? 'exact';

  return (
    <Column crossStretch gap={4} className="flex-1 min-h-0 w-full overflow-y-auto p-2">
      {(inviteTxtKey)
        ? (
            <span className="px-2 text-center text-pxTxtMuted">
              {translate(
                inviteTxtKey,
                'statements',
                { inviter: state.invite?.prtnrInvtInfo?.source?.cstmrLbl },
              )}
            </span>
          )
        : null}

      <span className="px-2 mb-2 text-center">
        {`${translate('createPartnerAccount1', 'statements')} ${
          translate('createPartnerAccount2', 'statements')}`}
      </span>

      <TextInput
        id="fname"
        name="fname"
        placeholder={translate('firstName')}
        className="mx-auto"
        onChange={handleChange}
        value={state.fname ?? ''}
        invalid={state.fnameTouched && (!validation.valid.fname)}
        focus
      />

      <TextInput
        id="lname"
        name="lname"
        placeholder={translate('lastName')}
        className="mx-auto"
        onChange={handleChange}
        value={state.lname ?? ''}
        invalid={state.lnameTouched && (!validation.valid.lname)}
      />

      {(emailRule === 'domain')
        ? (
            <Row gap={1} className="mx-auto">
              <TextInput
                id="email"
                name="email"
                placeholder={translate('email')}
                fillType="off"
                onChange={handleEmailLocalPartChange}
                value={(state.email ?? '').split('@')[0]}
                invalid={state.emailTouched && (!validation.valid.email)}
              />

              <span className="whitespace-pre text-pxTxtMuted">{`@${invitedDomain}`}</span>
            </Row>
          )
        : (
            <TextInput
              id="email"
              name="email"
              placeholder={translate('email')}
              className="mx-auto"
              onChange={handleChange}
              value={state.email ?? ''}
              invalid={state.emailTouched && (!validation.valid.email)}
              disabled={emailRule === 'exact'}
            />
          )}

      <TextInput
        id="rlDesc"
        name="rlDesc"
        placeholder={translate('companyTitle')}
        className="mx-auto"
        onChange={handleChange}
        value={state.rlDesc ?? ''}
        invalid={state.rlDescTouched && (!validation.valid.rlDesc)}
      />

      <InviteAckRefusal
        piid={state.piid}
        inviter={state.invite?.prtnrInvtInfo?.source?.cstmrLbl}
        onRefused={state.onRefused}
      />
    </Column>
  );
}

export default InviteAckIdentityPage;
